import { z } from "zod";
import { generationProgressSchema } from "./generation-progress";
import { RUN_STATUS_OPTIONS } from "./run-state";
import { DEFAULT_IMAGE_SIZE, IMAGE_MODEL_OPTIONS, IMAGE_SIZE_OPTIONS, REASONING_EFFORT_OPTIONS, TEXT_MODEL_OPTIONS } from "./model-preferences";

export const shellSurfaceSchema = z.enum(["web", "windows"]);
export const shellApprovalGateSchema = z.enum([
  "brief",
  "storyline",
  "visual_direction",
]);
export const shellRunStatusSchema = z.enum(RUN_STATUS_OPTIONS);
export const SHELL_WORKFLOW_STAGE_IDS = ["intent", "direction", "creation", "delivery"] as const;
export const shellWorkflowStageIdSchema = z.enum(SHELL_WORKFLOW_STAGE_IDS);

const identifierSchema = z.string().trim().min(1).max(200);
const nonEmptyTextSchema = z.string().trim().min(1);
const artifactHashSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);

export const shellProjectCreateInputSchema = z
  .object({
    projectId: identifierSchema,
    idempotencyKey: identifierSchema,
    name: nonEmptyTextSchema,
    objective: nonEmptyTextSchema,
    audience: nonEmptyTextSchema,
    sourceNames: z.array(nonEmptyTextSchema).max(20),
    slideCount: z.number().int().min(3).max(30),
    surface: shellSurfaceSchema,
    textModel: z.enum(TEXT_MODEL_OPTIONS),
    imageModel: z.enum(IMAGE_MODEL_OPTIONS).optional(),
    imageSize: z.enum(IMAGE_SIZE_OPTIONS).default(DEFAULT_IMAGE_SIZE),
    reasoningEffort: z.enum(REASONING_EFFORT_OPTIONS),
  })
  .strict();

export const shellApprovalReviewSchema = z
  .object({
    projectId: identifierSchema,
    gate: shellApprovalGateSchema,
    artifactHash: artifactHashSchema,
    artifactRevision: z.number().int().positive(),
    status: z.enum(["approved", "changes_requested"]),
    reason: nonEmptyTextSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.status === "changes_requested" && !value.reason) {
      context.addIssue({
        code: "custom",
        message: "Change requests require a reason.",
        path: ["reason"],
      });
    }
  });

const approvalSummarySchema = z
  .object({
    gate: shellApprovalGateSchema,
    label: nonEmptyTextSchema,
    status: z.enum(["completed", "current", "pending"]),
  })
  .strict();

const pendingApprovalSchema = z
  .object({
    gate: shellApprovalGateSchema,
    artifactHash: artifactHashSchema,
    artifactRevision: z.number().int().positive(),
    title: nonEmptyTextSchema,
    summary: nonEmptyTextSchema,
    details: z.array(nonEmptyTextSchema).min(1),
  })
  .strict();

export const shellStageRecordSchema = z
  .object({
    stage: shellWorkflowStageIdSchema,
    label: nonEmptyTextSchema,
    status: z.enum(["completed", "current", "pending"]),
    summary: nonEmptyTextSchema,
    // A macro phase contains up to 30 slide entries plus shared metadata.
    details: z.array(nonEmptyTextSchema).max(40),
  })
  .strict();

const slidePreviewSchema = z
  .object({
    slideId: identifierSchema,
    order: z.number().int().positive(),
    title: nonEmptyTextSchema,
    purpose: nonEmptyTextSchema,
    stateLabel: nonEmptyTextSchema,
  })
  .strict();

export const shellSnapshotSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    projectId: identifierSchema,
    projectName: nonEmptyTextSchema,
    surface: shellSurfaceSchema,
    runStatus: shellRunStatusSchema,
    sourceNames: z.array(nonEmptyTextSchema),
    stageRecords: z.array(shellStageRecordSchema).length(4).optional(),
    approvals: z.array(approvalSummarySchema).length(3),
    pendingApproval: pendingApprovalSchema.optional(),
    progress: z
      .object({
        completed: z.number().int().nonnegative(),
        total: z.number().int().positive(),
        label: nonEmptyTextSchema,
      })
      .strict(),
    slides: z.array(slidePreviewSchema),
    deliveryReady: z.boolean(),
    portableContinuationReady: z.boolean(),
    recovery: z
      .object({
        retryable: z.boolean(),
        errorCode: nonEmptyTextSchema,
        nodeId: nonEmptyTextSchema.optional(),
      })
      .strict()
      .optional(),
    recentEvent: nonEmptyTextSchema,
  })
  .strict();

export const shellDownloadSchema = z
  .object({
    fileName: nonEmptyTextSchema,
    mediaType: nonEmptyTextSchema,
    contentBase64: z.string().min(1),
    sha256: artifactHashSchema,
  })
  .strict();

export const shellCommandSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("create_project"),
      operationId: z.uuid().optional(),
      input: shellProjectCreateInputSchema,
    })
    .strict(),
  z.object({ type: z.literal("inspect"), projectId: identifierSchema, operationId: z.uuid().optional() }).strict(),
  z.object({ type: z.literal("read_snapshot"), projectId: identifierSchema }).strict(),
  z.object({ type: z.literal("retry_planning"), projectId: identifierSchema, operationId: z.uuid().optional() }).strict(),
  z.object({ type: z.literal("inspect_progress"), projectId: identifierSchema, operationId: z.uuid().optional() }).strict(),
  z
    .object({ type: z.literal("review_approval"), input: shellApprovalReviewSchema, operationId: z.uuid().optional() })
    .strict(),
  z
    .object({
      type: z.enum(["export_pptx", "export_portable"]),
      projectId: identifierSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("open_portable"),
      contentBase64: z.string().min(1),
      surface: z.literal("windows"),
    })
    .strict(),
]);

export const shellCommandResultSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("progress"), value: generationProgressSchema.nullable() }).strict(),
  z.object({ kind: z.literal("snapshot"), value: shellSnapshotSchema }).strict(),
  z.object({ kind: z.literal("download"), value: shellDownloadSchema }).strict(),
]);

export type ShellSurface = z.infer<typeof shellSurfaceSchema>;
export type ShellApprovalGate = z.infer<typeof shellApprovalGateSchema>;
export type ShellWorkflowStageId = z.infer<typeof shellWorkflowStageIdSchema>;
export type ShellStageRecord = z.infer<typeof shellStageRecordSchema>;
export type ShellProjectCreateInput = z.infer<typeof shellProjectCreateInputSchema>;
export type ShellApprovalReview = z.infer<typeof shellApprovalReviewSchema>;
export type ShellSnapshot = z.infer<typeof shellSnapshotSchema>;
export type ShellDownload = z.infer<typeof shellDownloadSchema>;
export type ShellCommand = z.infer<typeof shellCommandSchema>;
export type ShellCommandResult = z.infer<typeof shellCommandResultSchema>;

export interface ShellCommandPort {
  execute(command: ShellCommand): Promise<ShellCommandResult>;
}
